import React, { useMemo, useState } from 'react'
import { useCohort, cohortsQueryKeys } from '@/hooks/useCohorts'
import { Button, ButtonGroup, Card, CardContent, Skeleton, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@duck/ui'
import { useTar } from '@/modules/i18n'
import { usePartnerTar } from '@/hooks/usePartnerTar'
import { CohortPeopleList } from './People/CohortPeopleList'
import { buildCohortPageData } from './cohortPageData'
import { usePersons } from '@/hooks/usePersons'
import { useTaskDisplayNames } from '@/modules/entityLookups'
import { AssignmentsList } from './Assignments/AssignmentsList'
import { AddCohortPersonDialog } from './People/AddCohortPersonDialog'
import { CreateCohortDialog } from '@/components/PartnerCohorts/CreateCohortDialog'
import { DeleteDialog } from '@/components/DeleteDialog/DeleteDialog'
import { Plus, Settings, PencilIcon, X } from 'lucide-react'
import { useNavigate } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/hooks/useAuth'
import { apis } from '@/modules/apiClients'
import { PartnerService } from '@duck/api-definitions'
import { useOrganizationSettings } from '@/hooks/useOrganizationSettings'

interface CohortPageProps {
  cohortId: string
}

export const CohortPage: React.FC<CohortPageProps> = ({ cohortId }) => {
  const { cohort, assignmentsDetails, isLoading, isError, error, refetch } = useCohort(cohortId)
  const { persons } = usePersons(cohort?.personIds ?? [])
  const { t } = useTar()
  const { t: tP } = usePartnerTar()
  const { organizationId } = useAuth()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [view, setView] = useState<'all' | 'assignments'>('all')
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [isCloseDialogOpen, setIsCloseDialogOpen] = useState(false)
  const { getOrgSetting } = useOrganizationSettings()

  const { allPersons, taskAssignments } = useMemo(() => {
    if (!cohort || !assignmentsDetails || !persons || persons.length === 0) {
      return { allPersons: [], taskAssignments: {} }
    }
    return buildCohortPageData(cohort, assignmentsDetails, persons)
  }, [cohort, assignmentsDetails, persons])
  const taskIds = useMemo(() => Object.keys(taskAssignments), [taskAssignments])
  const { getDisplayName } = useTaskDisplayNames(taskIds)

  // Calculate open assignments count from assignmentsDetails
  const openAssignmentsCount = useMemo(() => {
    if (!assignmentsDetails || assignmentsDetails.length === 0) return 0

    // Group assignmentsDetails by assignmentId
    const assignmentsByAssignmentId = assignmentsDetails.reduce((acc, detail) => {
      if (!acc[detail.assignmentId]) {
        acc[detail.assignmentId] = []
      }
      acc[detail.assignmentId].push(detail)
      return acc
    }, {} as Record<string, typeof assignmentsDetails>)

    // Count assignments where submissions < personIds.length
    let openCount = 0
    for (const assignmentId in assignmentsByAssignmentId) {
      const details = assignmentsByAssignmentId[assignmentId]
      const submissions = details.filter(d => d.status === PartnerService.AssignmentDetailsStatus.Submitted).length
      const totalPersons = details.length

      if (submissions < totalPersons) {
        openCount++
      }
    }

    return openCount
  }, [assignmentsDetails])

  // Build warning message if there are open assignments
  const openAssignmentsWarning = useMemo(() => {
    if (openAssignmentsCount > 0) {
      return tP('openAssignmentsWarning', { count: openAssignmentsCount })
    }
    return undefined
  }, [openAssignmentsCount, tP])

  const invitationEmails = getOrgSetting('invitationEmails') ?? false


  if (isError || (!isLoading && !cohort)) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            {tP('cohort')}
          </h1>
        </div>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-8">
              <p className="text-muted-foreground">
                {error
                  ? t('common.errorLoading', { item: tP('cohorts').toLowerCase() })
                  : t('common.notFound', { item: tP('cohorts').toLowerCase() })}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          {isLoading || !cohort ? (
            <>
              <Skeleton className="h-9 w-1/3" />
              <Skeleton className="h-4 w-1/2 mt-2" />
            </>
          ) : (
            <>
              <h1 className="text-3xl font-bold text-foreground">{cohort.name}</h1>
              {cohort.description && (
                <p className="mt-2 text-sm text-muted-foreground">{cohort.description}</p>
              )}
            </>
          )}
        </div>
        {!isLoading && cohort && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <Settings className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => {
                setIsEditDialogOpen(true)
              }}>
                <PencilIcon className="h-4 w-4 mr-2" />
                {tP('editDetails')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => {
                setIsCloseDialogOpen(true)
              }}>
                <X className="h-4 w-4 mr-2" />
                {tP('closeCohort')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      <div className="flex flex-row gap-2 justify-between">
        <ButtonGroup>
          <Button
            variant="outline"
            className={view === 'all' ? 'bg-primary/60' : ''}
            onClick={() => setView('all')}

          >
            {tP('people')}
          </Button>
          <Button
            variant="outline"
            className={view === 'assignments' ? 'bg-primary/60' : ''}
            onClick={() => setView('assignments')}

          >
            {invitationEmails ? t('partner.sentAssignments') : t('partner.assignments')}
          </Button>
        </ButtonGroup>
        <Button onClick={() => setIsDialogOpen(true)} >
          <Plus className="h-4 w-4" />
          <span className="hidden md:inline">{tP('addPeople')}</span>
        </Button>
      </div>

      <AddCohortPersonDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        cohortId={cohortId}
        onPersonAdded={() => {
          refetch()
        }}
      />
      <CreateCohortDialog
        isOpen={isEditDialogOpen}
        onClose={() => setIsEditDialogOpen(false)}
        cohort={cohort || null}
        onCohortUpdated={() => {
          refetch()
        }}
      />
      <DeleteDialog
        open={isCloseDialogOpen}
        onOpenChange={setIsCloseDialogOpen}
        title={tP('closeCohort')}
        description={tP('closeCohortDescription')}
        deleteButtonText={tP('closeCohort')}
        warning={openAssignmentsWarning}
        onDelete={async () => {
          if (!cohort || !organizationId) {
            throw new Error('Cohort or organization ID is missing')
          }

          const updateCohortRequest: PartnerService.UpdateCohortRequest = {
            name: cohort.name,
            description: cohort.description,
            organizationId,
            type: cohort.type,
            isActive: false,
            personIds: cohort.personIds,
          }

          await apis.partnerService.cohortsCohortIdPut(cohortId, updateCohortRequest)

          // Invalidate cohorts query to refetch for SideBarPartner
          await queryClient.invalidateQueries({ queryKey: cohortsQueryKeys.user() })

          // Navigate to PartnerDashboard
          navigate({ to: '/partner/dashboard' })
        }}
      />
      {isLoading ? (
        view === 'assignments' ? (
          <AssignmentsList
            taskAssignments={{}}
            assignmentsDetails={[]}
            onAssignmentChange={refetch}
            skeleton={true}
          />
        ) : (
          <CohortPeopleList listTitle={tP('people')} peopleDetails={[]} cohortId={cohortId} handleCohortDataChange={refetch} skeleton={true} />
        )
      ) : view === 'all' ? (
        <CohortPeopleList key={cohortId} listTitle={tP('people')} peopleDetails={allPersons} cohortId={cohortId} handleCohortDataChange={refetch} onAddPeople={() => setIsDialogOpen(true)} />
      ) : view === 'assignments' ? (
        <AssignmentsList
          taskAssignments={taskAssignments}
          assignmentsDetails={assignmentsDetails}
          onAssignmentChange={refetch}
        />
      ) : (
        <CohortPeopleList
          listTitle={t('partner.sentAssignments', { taskName: getDisplayName(view) })}
          peopleDetails={taskAssignments[view] ?? []}
          cohortId={cohortId}
          handleCohortDataChange={refetch}
          listMode="task"
        />
      )}
    </div>
  )
}
